import { useState, useEffect } from 'react'
import {
  getSubmittalDetails,
  getSubmittalSchema,
  getActivityLogs,
} from '@/services/submittalService'
import { getFullJson, extractCombinedSchema } from '@/hooks/useEform'
import { capitalize } from '@/utils/helpers'
import useDataElements from '@/hooks/useDataElements'

const initialState = {
  payload: null,
  schema: null,
  elementName: null,
  status: null,
  modified: null,
  receiverOrgName: null,
  projectContractName: null,
  submissionMonth: null,
  loading: false,
  error: null,
  notFound: false,
}

export const useSubmittalDetail = (submittalId, onError) => {
  const [state, setState] = useState(initialState)
  const [activityLogs, setActivityLogs] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const { dataElements } = useDataElements()

  const refetch = () => setRefreshKey(k => k + 1)

  useEffect(() => {
    if (!submittalId) return

    let aborted = false
    setState(s => ({ ...s, loading: true, error: null }))

    const load = async () => {
      const {
        payload: rawPayload,
        data_element_id: dataElementId,
        status,
        modified,
        receiver_org_name: receiverOrgName,
        submission_month: submissionMonth,
      } = await getSubmittalDetails(submittalId)

      const projectContractName = rawPayload?.project_details?.project_contract_name ?? null

      const cached = dataElements?.find(
        el => el.dataElementId === dataElementId && el.elementSchema
      )
      const [schemaData, logs] = await Promise.all([
        cached ? Promise.resolve(cached) : getSubmittalSchema(dataElementId),
        getActivityLogs(submittalId).catch(err => {
          console.error('Failed to fetch activity logs:', err)
          return []
        }),
      ])
      const schemaItem = Array.isArray(schemaData) ? schemaData[0] : schemaData
      const { elementSchema: rawSchema, elementName } = schemaItem
      const schema = extractCombinedSchema(rawSchema, rawPayload)
      const payload = getFullJson(schema, rawPayload)

      if (!aborted) {
        setState({
          payload,
          schema,
          elementName,
          status,
          modified,
          receiverOrgName,
          projectContractName,
          submissionMonth,
          loading: false,
          error: null,
        })
        setActivityLogs(logs ?? [])
      }
    }

    load().catch(err => {
      if (!aborted) {
        const is404 = err.response?.status === 404
        const message = capitalize(err.response?.data?.detail) || err.message
        setState(s => ({ ...s, loading: false, error: message, notFound: is404 }))
      }
    })

    return () => {
      aborted = true
    }
  }, [submittalId, refreshKey])

  return { state, activityLogs, refetch }
}
